import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Flex, Modal, Stack, UnstyledButton } from '@mantine/core';
import { Button } from 'src/design-system/Button';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useFamilyMeetingParticipants } from 'src/meetings/hooks/useFamilyMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useFamilyMeetings';
import { AttendeeRow } from './AttendeeRow';
import { type Attendee, useMeetingAttendees } from './useMeetingAttendees';

type Props = {
  opened: boolean;
  onClose: () => void;
  familyId: string;
  meeting: FamilyMeeting;
};

const addOtherAttendeesStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  textAlign: 'center',
  fontFamily: 'Manrope',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '18px',
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationSkipInk: 'none',
  textDecorationThickness: '0.9px',
  textUnderlineOffset: '1.56px',
  textUnderlinePosition: 'from-font',
  paddingTop: 3,
  paddingBottom: 3,
};

export function AddExpectedAttendeesModal({ opened, onClose, familyId, meeting }: Props) {
  const attendees = useMeetingAttendees(familyId, meeting);
  const { addParticipant, removeParticipant } = useFamilyMeetingParticipants(familyId);
  const navigate = useNavigate();
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  const isChecked = (attendee: Attendee) => overrides[attendee.id] ?? attendee.defaultChecked;

  const toggle = (attendee: Attendee) =>
    setOverrides(previous => ({ ...previous, [attendee.id]: !isChecked(attendee) }));

  async function handleAdd() {
    const operations: Promise<unknown>[] = [];
    for (const attendee of attendees) {
      const checked = isChecked(attendee);
      if (checked && !attendee.defaultChecked) {
        operations.push(
          addParticipant.mutateAsync({ eventId: meeting.id, body: attendee.addBody }),
        );
      } else if (!checked && attendee.defaultChecked && attendee.participantId) {
        operations.push(
          removeParticipant.mutateAsync({
            eventId: meeting.id,
            participantId: attendee.participantId,
          }),
        );
      }
    }

    if (operations.length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      await Promise.all(operations);
      onClose();
    } catch (error) {
      notifyAndLogError(
        'Failed to update attendees',
        'Could not update the meeting attendees. Please try again.',
        error,
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Add expected attendees" size={440}>
      <Stack gap="xxxs">
        {attendees.map(attendee => (
          <AttendeeRow
            key={attendee.id}
            name={attendee.name}
            role={attendee.role}
            checked={isChecked(attendee)}
            onToggle={() => toggle(attendee)}
            ariaLabel={`${attendee.name} attending`}
          />
        ))}
      </Stack>

      <Box mt="lg">
        <Button fullWidth onClick={handleAdd} loading={isSaving} disabled={isSaving}>
          Add
        </Button>
      </Box>

      <Flex justify="center" mt="xs">
        <UnstyledButton
          style={addOtherAttendeesStyle}
          onClick={() => {
            onClose();
            navigate(`/family/${familyId}/meetings/${meeting.id}/add-attendee`);
          }}
        >
          Add other attendees
        </UnstyledButton>
      </Flex>
    </Modal>
  );
}
